import { useEffect, useState, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Slider,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Grid,
  Paper,
  LinearProgress,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import VolumeOffRoundedIcon from '@mui/icons-material/VolumeOffRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import SkipPreviousRoundedIcon from '@mui/icons-material/SkipPreviousRounded';
import SkipNextRoundedIcon from '@mui/icons-material/SkipNextRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import MusicNoteRoundedIcon from '@mui/icons-material/MusicNoteRounded';
import QueueMusicRoundedIcon from '@mui/icons-material/QueueMusicRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import EventRoundedIcon from '@mui/icons-material/EventRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import PlayCircleRoundedIcon from '@mui/icons-material/PlayCircleRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import FormatListBulletedRoundedIcon from '@mui/icons-material/FormatListBulletedRounded';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import ShuffleRoundedIcon from '@mui/icons-material/ShuffleRounded';
import FormatListNumberedRoundedIcon from '@mui/icons-material/FormatListNumberedRounded';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import LockOpenRoundedIcon from '@mui/icons-material/LockOpenRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import AlbumRoundedIcon from '@mui/icons-material/AlbumRounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import DnsRoundedIcon from '@mui/icons-material/DnsRounded';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import { Zone } from '../models/Zone';
import { BlockedTrack } from '../models/BlockedTrack';
// Import new zone service for improved playlist handling
import * as zoneService from '../services/newZoneService';
// Schedule Manager is now integrated into the ZoneForm
import { useAuth } from '../hooks/useAuth';
import { useSnackbar } from 'notistack';
import { UserRole } from '../models/User';
import { ZoneForm } from '../components/Zones/ZoneForm';
import { ZoneFilterSidebar } from '../components/Zones/ZoneFilterSidebar';
import { CategoryManagementModal } from '../components/Zones/CategoryManagementModal';
import { ZoneCategory, ZoneCategoryFilter } from '../models/ZoneCategory';
import * as zoneCategoryService from '../services/zoneCategoryService';
import { useTheme } from '../contexts/ThemeContext';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

// Helper function to filter zones by category
const getFilteredZones = (zones: Zone[], categoryId: number | null): Zone[] => {
  if (!categoryId) return zones;
  return zones.filter(zone => zone.categoryId === categoryId);
};

// Helper function to format progress time (MM:SS) with improved accuracy
const formatProgressTime = (progressPercent: number, duration: string | undefined): string => {
  try {
    // Check if duration is valid
    if (!duration) {
      return "00:00";
    }
    
    // Parse duration string (format: "MM:SS")
    const durationParts = duration.split(':');
    
    // Ensure we have valid numbers
    if (durationParts.length !== 2) {
      console.warn(`Invalid duration format: ${duration}, expected MM:SS`);
      return "00:00";
    }
    
    // Parse duration parts to numbers, with error checking
    const minutes = parseInt(durationParts[0], 10);
    const seconds = parseInt(durationParts[1], 10);
    
    if (isNaN(minutes) || isNaN(seconds)) {
      console.warn(`Invalid duration parts: minutes=${durationParts[0]}, seconds=${durationParts[1]}`);
      return "00:00";
    }
    
    const totalSeconds = (minutes * 60) + seconds;
    
    // Ensure progress is between 0-100
    const clampedProgress = Math.max(0, Math.min(100, progressPercent || 0));
    
    // Calculate elapsed seconds based on progress percentage (more precise with decimals)
    const elapsedSeconds = Math.floor(totalSeconds * (clampedProgress / 100));
    
    // Format as MM:SS
    const elapsedMinutes = Math.floor(elapsedSeconds / 60);
    const elapsedSecondsRemainder = elapsedSeconds % 60;
    
    return `${elapsedMinutes.toString().padStart(2, '0')}:${elapsedSecondsRemainder.toString().padStart(2, '0')}`;
  } catch (error) {
    console.error("Error formatting progress time:", error);
    return "00:00";
  }
};

export const Zones = () => {
  const { user, userRole, isHotelManager } = useAuth();
  const currentUserRole = userRole || UserRole.HotelUser; // Default for safer access
  const { enqueueSnackbar } = useSnackbar();
  const { darkMode } = useTheme();
  const [zones, setZones] = useState<Zone[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [categories, setCategories] = useState<ZoneCategoryFilter[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [filterCollapsed, setFilterCollapsed] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editDialogInitialTab, setEditDialogInitialTab] = useState(0);
  const [selectedZoneId, setSelectedZoneId] = useState<number | null>(null);
  const [selectedZone, setSelectedZone] = useState<Zone | null>(null);
  const [queueDialogOpen, setQueueDialogOpen] = useState(false);
  const [selectedQueueZoneId, setSelectedQueueZoneId] = useState<number | null>(null);
  const [upcomingTracks, setUpcomingTracks] = useState<any[]>([]);
  const [loadingUpcomingTracks, setLoadingUpcomingTracks] = useState(false);
  const [blockedTracksDialogOpen, setBlockedTracksDialogOpen] = useState(false);
  const [categoryManagementOpen, setCategoryManagementOpen] = useState(false);
  const [blockedTracks, setBlockedTracks] = useState<BlockedTrack[]>([]);
  const [loadingBlockedTracks, setLoadingBlockedTracks] = useState(false);
  const [selectedHotelId, setSelectedHotelId] = useState<number | null>(null);
  // Confirmation dialog states
  const [blockConfirm, setBlockConfirm] = useState<{open: boolean, zoneId: number | null, zoneName: string, trackTitle: string}>({open: false, zoneId: null, zoneName: '', trackTitle: ''});
  const [shuffleConfirm, setShuffleConfirm] = useState<{open: boolean, zoneId: number | null, currentShuffle: boolean}>({open: false, zoneId: null, currentShuffle: false});
  const [refreshConfirm, setRefreshConfirm] = useState<{open: boolean, zoneId: number | null, zoneName: string}>({open: false, zoneId: null, zoneName: ''});

  // Volume confirmation states
  const [pendingVolumeChanges, setPendingVolumeChanges] = useState<{[zoneId: number]: number}>({});
  const [volumeConfirmDialog, setVolumeConfirmDialog] = useState<{open: boolean, zoneId: number | null, newVolume: number, oldVolume: number}>({
    open: false, zoneId: null, newVolume: 0, oldVolume: 0
  });
  
  // Removed standalone Event Manager as it's now integrated into the zone edit form
  
  // Track progress timers
  const progressTimers = useRef<{[zoneId: number]: NodeJS.Timeout}>({});

  const fetchZones = async () => {
    try {
      setLoading(true);
      const data = await zoneService.getZones();
      setZones(data);
    } catch (error) {
      console.error('Error fetching zones:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      setCategoriesLoading(true);
      const data = await zoneCategoryService.getZoneCategories();
      const filterData = zoneCategoryService.convertCategoriesToFilters(data, zones);
      setCategories(filterData);
    } catch (error) {
      console.error('Error loading zone categories:', error);
      enqueueSnackbar('Zone kategorileri yüklenirken hata oluştu', { variant: 'error' });
    } finally {
      setCategoriesLoading(false);
    }
  };
  
  // Function to refresh a specific zone
  const refreshZone = async (zoneId: number) => {
    try {
      const updatedZone = await zoneService.getZoneById(zoneId);
      setZones(prevZones => 
        prevZones.map(zone => 
          zone.id === zoneId ? updatedZone : zone
        )
      );
    } catch (error) {
      console.error(`Error refreshing zone ${zoneId}:`, error);
    }
  };
  
  // Add partial refresh for current track information every 5 seconds when playing
  const zonesRef = useRef(zones);
  
  // Update the ref when zones change
  useEffect(() => {
    zonesRef.current = zones;
  }, [zones]);
  
  // Check if any modal is open
  const isAnyModalOpen = useRef(false);
  const disableAutoRefresh = useRef(false);
  
  // Update isAnyModalOpen when modal states change
  useEffect(() => {
    isAnyModalOpen.current = openCreateDialog || editDialogOpen || deleteDialogOpen || 
                            queueDialogOpen || blockedTracksDialogOpen || categoryManagementOpen;
  }, [openCreateDialog, editDialogOpen, deleteDialogOpen, queueDialogOpen, blockedTracksDialogOpen, categoryManagementOpen]);
  
  // Listen for openCreateZoneDialog event from AppBar
  useEffect(() => {
    const handler = () => setOpenCreateDialog(true);
    window.addEventListener('openCreateZoneDialog', handler);
    return () => window.removeEventListener('openCreateZoneDialog', handler);
  }, []);

  // Load data on component mount
  useEffect(() => {
    fetchZones();
    loadCategories();
  }, []);

  // Reload categories when zones change
  useEffect(() => {
    if (zones.length > 0) {
      loadCategories();
    }
  }, [zones]);

  useEffect(() => {
    // Store the current state of modals when the effect runs
    const isModalOpen = isAnyModalOpen.current;

    const fetchCurrentTracks = async () => {
      // Check again if any modal is open to prevent UI flickering
      // This helps catch modals that might have opened after the interval was created
      if (isAnyModalOpen.current) {
        console.log("Skipping data refresh because a modal is open");
        return;
      }

      // Get current zones
      const currentZones = zonesRef.current;

      // Always refresh all zones data to catch event-triggered changes
      // This is crucial to keep UI in sync with event system
      console.log("Refreshing data for all zones to detect event changes");

      try {
        // Create promises for all zones, not just playing ones
        const promises = currentZones.map(async zone => {
          try {
            // Role-based zone refresh: HotelUser uses getPlaybackProgress, others use getZoneById
            if (currentUserRole === UserRole.HotelUser) {
              const progress = await zoneService.getPlaybackProgress(zone.id);
              if (progress) {
                // Create a zone-like object from progress data
                return {
                  ...zone,
                  isPlaying: progress.isPlaying,
                  volume: progress.volume,
                  currentTrack: zone.currentTrack ? {
                    ...zone.currentTrack,
                    progress: progress.progressPercent
                  } : undefined
                };
              }
              return zone; // Return existing zone if progress fails
            } else {
              // HotelManager uses original method
              return await zoneService.getZoneById(zone.id);
            }
          } catch (error: any) {
            // Only log non-403 errors for HotelUsers
            if (currentUserRole !== UserRole.HotelUser || error.response?.status !== 403) {
              console.error(`Error fetching zone ${zone.id}:`, error);
            }
            return zone; // Return existing zone on error
          }
        });

        const updatedZones = await Promise.all(promises);

        // Do one more check before updating the UI to ensure no modal opened during the fetch
        if (isAnyModalOpen.current) {
          console.log("Modal opened during fetch, discarding results");
          return;
        }

        // Update all zones, preserving UI state as needed
        setZones(prevZones => {
          const newZones = prevZones.map(zone => {
            const updatedZone = updatedZones.find(
              updated => updated && updated.id === zone.id
            );

            if (updatedZone) {
              // Check for event-triggered changes - different play state or track
              const playStateChanged = zone.isPlaying !== updatedZone.isPlaying;
              const trackChanged = zone.currentTrack?.id !== updatedZone.currentTrack?.id;

              if (playStateChanged) {
                console.log(`Zone ${zone.id} play state changed via event: ${zone.isPlaying} -> ${updatedZone.isPlaying}`);
              }

              if (trackChanged) {
                console.log(`Zone ${zone.id} track changed via event`);
              }

              // Keep track progress if it exists, or initialize it
              if (updatedZone.isPlaying && updatedZone.currentTrack) {
                // If track has changed, reset progress
                if (trackChanged) {
                  updatedZone.currentTrack.progress = 0;
                  // Update the lastTrackIdsRef for the new track
                  if (updatedZone.currentTrack.id) {
                    lastTrackIdsRef.current[zone.id] = updatedZone.currentTrack.id;
                  }
                } else if (zone.currentTrack && zone.currentTrack.progress !== undefined) {
                  // Preserve progress for the same track
                  updatedZone.currentTrack.progress = zone.currentTrack.progress;
                } else {
                  // Initialize progress
                  updatedZone.currentTrack.progress = 0;
                }
              }

              // Add an indicator if this change was triggered by an event
              if ((playStateChanged || trackChanged) && (updatedZone.trackedEventId ?? 0) > 0) {
                updatedZone.eventControlled = true;

                // Auto-clear the indicator after 5 seconds
                setTimeout(() => {
                  setZones(z => z.map(item =>
                    item.id === updatedZone.id ? { ...item, eventControlled: false } : item
                  ));
                }, 5000);
              }

              return updatedZone;
            }
            return zone;
          });

          return newZones;
        });
      } catch (error) {
        console.error('Error in refresh:', error);
      }
    };

    // Create a refresh interval only if no modal is open
    let tracksInterval: NodeJS.Timeout | null = null;

    if (!isModalOpen) {
      // Use a faster polling rate (3 seconds) to detect event changes more promptly
      tracksInterval = setInterval(fetchCurrentTracks, 3000);
      console.log("Created refresh interval");

      // Run an immediate fetch to ensure we have fresh data
      fetchCurrentTracks();
    } else {
      console.log("Not creating refresh interval because a modal is open");
    }

    return () => {
      if (tracksInterval) {
        clearInterval(tracksInterval);
        console.log("Cleared refresh interval");
      }
    };
  }, [openCreateDialog, editDialogOpen, deleteDialogOpen, queueDialogOpen, blockedTracksDialogOpen]); // Re-run when any modal state changes
  
  // Track last played track IDs for each zone
  const lastTrackIdsRef = useRef<{[zoneId: number]: number}>({});
  
  // Helper function to calculate elapsed seconds based on progress percentage
  const getElapsedSecondsFromProgress = (progress: number, durationInSeconds: number): number => {
    return Math.floor(durationInSeconds * (progress / 100));
  };

  // Helper function to get the progress percentage from elapsed seconds
  const getProgressFromElapsedSeconds = (elapsedSeconds: number, durationInSeconds: number): number => {
    return (elapsedSeconds / durationInSeconds) * 100;
  };

  // Define interface for stored progress data
  interface StoredProgressData {
    trackId: number;
    elapsedSeconds: number;
    timestamp: number;
    durationInSeconds: number;
  }

  // Store track progress in localStorage for persistence
  const storeTrackProgress = (zoneId: number, trackId: number, progress: number, durationInSeconds: number): void => {
    try {
      // Get elapsed seconds from progress percentage
      const elapsedSeconds = getElapsedSecondsFromProgress(progress, durationInSeconds);
      
      // Create a timestamp to calculate elapsed time after page refresh
      const timestamp = Date.now();
      
      // Store the progress data
      localStorage.setItem(`zoneProgress_${zoneId}`, JSON.stringify({
        trackId,
        elapsedSeconds,
        timestamp,
        durationInSeconds
      } as StoredProgressData));
    } catch (error) {
      console.error("Error storing track progress:", error);
    }
  };

  // Retrieve track progress from localStorage
  const retrieveTrackProgress = (zoneId: number, trackId: number, durationInSeconds: number): number => {
    try {
      const storedProgressData = localStorage.getItem(`zoneProgress_${zoneId}`);
      
      if (!storedProgressData) return 0;
      
      const { trackId: storedTrackId, elapsedSeconds, timestamp, durationInSeconds: storedDuration } = JSON.parse(storedProgressData) as StoredProgressData;
      
      // Return 0 if track ID has changed
      if (storedTrackId !== trackId) return 0;
      
      // Calculate time elapsed since the progress was stored
      const elapsedSinceStored = (Date.now() - timestamp) / 1000;
      
      // Add the time elapsed since storing to the stored elapsed seconds
      const currentElapsedSeconds = elapsedSeconds + elapsedSinceStored;
      
      // If we've exceeded the duration, default to 0 (track has likely ended)
      if (currentElapsedSeconds >= durationInSeconds) {
        return 0;
      }
      
      // Calculate the current progress percentage
      return getProgressFromElapsedSeconds(currentElapsedSeconds, durationInSeconds);
    } catch (error) {
      console.error("Error retrieving track progress:", error);
      return 0;
    }
  };

  // Real-time progress tracking with backend synchronization
  useEffect(() => {
    // Don't set up timers if modals are open or auto-refresh is disabled
    if (isAnyModalOpen.current || disableAutoRefresh.current) {
      return;
    }
    
    // Clear any existing timers
    Object.values(progressTimers.current).forEach(timer => clearInterval(timer));
    progressTimers.current = {};
    
    // Create new timers for playing zones with backend sync
    zones.forEach(zone => {
      if (zone.isPlaying && zone.currentTrack) {
        // Use faster update interval for real-time backend sync
        const updateInterval = 1000; // 1 second for backend API calls
        
        // Get track ID for comparison
        const trackId = zone.currentTrack.id;
        
        // Create interval to fetch real-time progress from backend API
        progressTimers.current[zone.id] = setInterval(async () => {
          // Don't update if modals are open or auto-refresh is disabled
          if (isAnyModalOpen.current || disableAutoRefresh.current) {
            return;
          }
          
          try {
            // Fetch real-time progress from backend
            const progressData = await zoneService.getPlaybackProgress(zone.id);
            
            if (!progressData || !progressData.success) {
              return;
            }
            
            // Update zone with real backend progress
            setZones(prevZones => {
              // Find the current zone state
              const currentZone = prevZones.find(z => z.id === zone.id);
              
              // If zone is no longer playing in UI, clear this timer and exit
              if (!currentZone || !currentZone.isPlaying) {
                if (progressTimers.current[zone.id]) {
                  clearInterval(progressTimers.current[zone.id]);
                  delete progressTimers.current[zone.id];
                  console.log(`Auto-cleared backend progress timer for zone ${zone.id} (no longer playing)`);
                }
                return prevZones;
              }
              
              // Update zone with real progress from backend
              return prevZones.map(z => {
                if (z.id === zone.id && z.currentTrack) {
                  // Backend says zone is playing but UI shows it's not - sync the states
                  if (progressData.isPlaying !== z.isPlaying) {
                    console.log(`Syncing play state for zone ${zone.id}: UI=${z.isPlaying}, Backend=${progressData.isPlaying}`);
                  }
                  
                  // If progress is very high (>99%), prepare for track transition
                  if (progressData.progressPercent >= 99 && z.currentTrack.progress && z.currentTrack.progress < 99) {
                    console.log(`Track ending detected for zone ${zone.id} at ${progressData.progressPercent}%`);
                    
                    // Auto-refresh zone data when track is about to end
                    setTimeout(() => {
                      if (!isAnyModalOpen.current) {
                        zoneService.getZoneById(zone.id)
                          .then(updatedZone => {
                            if (!isAnyModalOpen.current) {
                              setZones(prevZones => 
                                prevZones.map(innerZ => 
                                  innerZ.id === zone.id ? {
                                    ...updatedZone,
                                    currentTrack: updatedZone.currentTrack ? {
                                      ...updatedZone.currentTrack,
                                      progress: 0 // Reset progress for new track
                                    } : undefined
                                  } : innerZ
                                )
                              );
                            }
                          })
                          .catch(err => console.error(`Error refreshing zone ${zone.id}:`, err));
                      }
                    }, 1000);
                  }
                  
                  // Update with real backend progress
                  return {
                    ...z,
                    isPlaying: progressData.isPlaying, // Sync play state
                    volume: progressData.volume, // Real-time volume from database
                    currentTrack: {
                      ...z.currentTrack,
                      progress: progressData.progressPercent,
                    }
                  };
              }
              return z;
            });
            });
          } catch (error) {
            console.error(`Error fetching progress for zone ${zone.id}:`, error);
          }
        }, updateInterval);
      }
    });
    
    // Cleanup on unmount
    return () => {
      Object.values(progressTimers.current).forEach(timer => clearInterval(timer));
    };
  }, [zones, editDialogOpen]); // Include editDialogOpen to properly respond to modal state changes

  // State-aware timer cleanup: Clear timers when zones stop playing
  useEffect(() => {
    zones.forEach(zone => {
      // If zone is not playing but has a timer, clear it
      if (!zone.isPlaying && progressTimers.current[zone.id]) {
        console.log(`Clearing timer for stopped zone ${zone.id}`);
        clearInterval(progressTimers.current[zone.id]);
        delete progressTimers.current[zone.id];
      }
    });
  }, [zones.map(z => `${z.id}-${z.isPlaying}`).join(',')]); // React to isPlaying changes

  useEffect(() => {
    fetchZones();
  }, []);
  
  // Handle opening queue dialog
  const handleOpenQueueDialog = async (zoneId: number) => {
    setSelectedQueueZoneId(zoneId);
    setQueueDialogOpen(true);
    setLoadingUpcomingTracks(true);
    
    try {
      // Tüm playlist parçalarını getir
      const tracks = await zoneService.getUpcomingTracks(zoneId, 999);
      setUpcomingTracks(tracks);
      
      // Log for debugging
      console.log(`Fetched ${tracks.length} upcoming tracks for zone ${zoneId}`);
    } catch (error) {
      console.error('Error fetching upcoming tracks:', error);
      setUpcomingTracks([]);
    } finally {
      setLoadingUpcomingTracks(false);
    }
  };
  
  // Handle closing queue dialog
  const handleCloseQueueDialog = () => {
    setQueueDialogOpen(false);
    setSelectedQueueZoneId(null);
    setUpcomingTracks([]);
  };
  
  // Refresh playlist for a specific zone
  const handleRefreshPlaylist = async (zoneId: number) => {
    try {
      console.log(`Manually refreshing playlist for zone ${zoneId}`);
      const zone = zones.find(z => z.id === zoneId);
      const result = await zoneService.refreshPlaylist(zoneId, zone?.name);
      
      if (result.success) {
        console.log(`Playlist refreshed successfully with ${result.trackCount} tracks`);
        // Refresh zone data after playlist update
        fetchZones();
      } else {
        console.error(`Failed to refresh playlist: ${result.message}`);
      }
    } catch (error) {
      console.error('Error refreshing playlist:', error);
    }
  };

  const handlePlayStop = async (zone: Zone) => {
    // Skip if any modal is open to prevent UI flickering
    if (isAnyModalOpen.current) {
      console.log("Skipping play/stop action while modal is open");
      return;
    }
    
    try {
      const wasPlaying = zone.isPlaying;
      
      if (wasPlaying) {
        // IMMEDIATE state update: Stop + Reset progress to 0
        setZones(zones.map(z => 
          z.id === zone.id ? { 
            ...z, 
            isPlaying: false,
            currentTrack: z.currentTrack ? {
              ...z.currentTrack,
              progress: 0  // Reset progress immediately on stop
            } : undefined
          } : z
        ));
        
        // Clear progress timer for this zone immediately
        if (progressTimers.current[zone.id]) {
          clearInterval(progressTimers.current[zone.id]);
          delete progressTimers.current[zone.id];
        }
        
        // When stopping, store the current progress in localStorage before stopping
        if (zone.currentTrack && zone.currentTrack.progress !== undefined) {
          const trackId = zone.currentTrack.id;
          const progress = zone.currentTrack.progress;
          
          // Get duration in seconds
          const durationParts = zone.currentTrack.duration.split(':');
          let durationInSeconds = 180; // Default
          try {
            durationInSeconds = (parseInt(durationParts[0], 10) * 60) + parseInt(durationParts[1], 10);
            
            // Sanity check
            if (isNaN(durationInSeconds) || durationInSeconds <= 0 || durationInSeconds > 1800) {
              durationInSeconds = 180;
            }
          } catch (error) {
            console.error("Error parsing duration for localStorage:", error);
          }
          
          // Store current progress
          storeTrackProgress(zone.id, trackId, progress, durationInSeconds);
        }
        
        // Make the API call to stop
        const stopResult = await zoneService.stopZone(zone.id, zone.name);
        if (!stopResult.success) {
          console.error('Error stopping zone:', stopResult);
          
          // ROLLBACK: Restore previous state on API failure
          setZones(zones.map(z => 
            z.id === zone.id ? { 
              ...z, 
              isPlaying: true,  // Restore playing state
              currentTrack: z.currentTrack ? {
                ...z.currentTrack,
                progress: zone.currentTrack?.progress || 0  // Restore progress
              } : undefined
            } : z
          ));
          
          // Show error to user
          alert(`Failed to stop zone: ${stopResult.message || 'Unknown error'}`);
          return; // Exit early on failure
        }
      } else {
        // We're starting playback
        
        // Get progress from localStorage if available for this track
        let initialProgress = 0;
        if (zone.currentTrack) {
          const trackId = zone.currentTrack.id;
          
          // Get duration in seconds
          const durationParts = zone.currentTrack.duration.split(':');
          let durationInSeconds = 180; // Default
          try {
            durationInSeconds = (parseInt(durationParts[0], 10) * 60) + parseInt(durationParts[1], 10);
            
            // Sanity check
            if (isNaN(durationInSeconds) || durationInSeconds <= 0 || durationInSeconds > 1800) {
              durationInSeconds = 180;
            }
          } catch (error) {
            console.error("Error parsing duration for localStorage:", error);
          }
          
          // Retrieve stored progress
          initialProgress = retrieveTrackProgress(zone.id, trackId, durationInSeconds);
        }
        
        // IMMEDIATE state update: Start from beginning (progress = 0)
        setZones(zones.map(z => {
          if (z.id === zone.id) {
            return { 
              ...z, 
              isPlaying: true,
              currentTrack: z.currentTrack ? {
                ...z.currentTrack,
                progress: 0  // Always start from 0 for true sync
              } : undefined
            };
          }
          return z;
        }));
        
        // Make the API call to play
        const playResult = await zoneService.playZone(zone.id, zone.name);
        if (!playResult.success) {
          console.error('Error playing zone:', playResult);
          
          // ROLLBACK: Restore previous state on API failure
          setZones(zones.map(z => 
            z.id === zone.id ? { 
              ...z, 
              isPlaying: false,  // Restore stopped state
              currentTrack: z.currentTrack ? {
                ...z.currentTrack,
                progress: zone.currentTrack?.progress || 0  // Restore previous progress
              } : undefined
            } : z
          ));
          
          // Show error to user
          alert(`Failed to start zone: ${playResult.message || 'Unknown error'}`);
          return; // Exit early on failure
        }
      }
      
      // Add a small delay before fetching updated state for UI consistency
      // Only fetch if no modal is open
      if (!isAnyModalOpen.current) {
        setTimeout(async () => {
          // Check again before fetching in case a modal opened during the timeout
          if (isAnyModalOpen.current) {
            console.log("Skipping post-action refresh because a modal is now open");
            return;
          }
          
          try {
            // Role-based zone update: HotelUser uses getPlaybackProgress, others use getZoneById
            if (currentUserRole === UserRole.HotelUser) {
              // For HotelUser: use getPlaybackProgress to avoid 403 errors
              const progress = await zoneService.getPlaybackProgress(zone.id);
              if (progress) {
                // Update zone state from progress data
                setZones(prevZones => 
                  prevZones.map(z => {
                    if (z.id === zone.id) {
                      return {
                        ...z,
                        isPlaying: progress.isPlaying,
                        volume: progress.volume,
                        currentTrack: z.currentTrack ? {
                          ...z.currentTrack,
                          progress: progress.progressPercent
                        } : undefined
                      };
                    }
                    return z;
                  })
                );
              }
            } else {
              // For HotelManager: use original getZoneById method
              const updatedZone = await zoneService.getZoneById(zone.id);
              
              // Ensure the track name has changed if we were stopping
              if (wasPlaying && updatedZone.isPlaying) {
                // If server still says playing after stop request, try stopping again
                console.warn('Zone still playing after stop request, retrying...');
                await zoneService.stopZone(zone.id, zone.name);
                
                // Get fresh state
                const finalZone = await zoneService.getZoneById(zone.id);
              
                // Update zone with fresh state
                setZones(prevZones => 
                  prevZones.map(z => z.id === zone.id ? finalZone : z)
                );
                return;
              }
              
              // Update just this one zone, preserving progress state
              setZones(prevZones => 
                prevZones.map(z => {
                  if (z.id === zone.id) {
                    if (updatedZone.isPlaying && !wasPlaying && updatedZone.currentTrack) {
                      // If now playing but wasn't before, check localStorage for a stored progress value
                      const trackId = updatedZone.currentTrack.id;
                      
                      // Get duration in seconds
                      const durationParts = updatedZone.currentTrack.duration.split(':');
                      let durationInSeconds = 180; // Default
                      try {
                        durationInSeconds = (parseInt(durationParts[0], 10) * 60) + parseInt(durationParts[1], 10);
                        
                        // Sanity check
                        if (isNaN(durationInSeconds) || durationInSeconds <= 0 || durationInSeconds > 1800) {
                          durationInSeconds = 180;
                        }
                      } catch (error) {
                        console.error("Error parsing duration for localStorage:", error);
                      }
                      
                      // Use the stored progress or start at 0
                      const initialProgress = retrieveTrackProgress(zone.id, trackId, durationInSeconds);
                      
                      return {
                        ...updatedZone,
                        currentTrack: {
                          ...updatedZone.currentTrack,
                          progress: initialProgress
                        }
                      };
                    }
                    
                    // For other cases, just use the updated zone
                    return updatedZone;
                  }
                  return z;
                })
              );
            }
          } catch (refreshError) {
            console.error(`Error fetching updated zone ${zone.id}:`, refreshError);
          }
        }, 300); // Small delay for better UI experience
      } // Close the if (!isAnyModalOpen.current) block
    } catch (error) {
      console.error('Error toggling playback:', error);
      alert('Error toggling playback: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  // Helper function to get display volume (pending or actual)
  const getDisplayVolume = (zone: Zone): number => {
    return pendingVolumeChanges[zone.id] ?? zone.volume;
  };

  // Helper function to check if zone has pending volume changes
  const hasPendingVolumeChange = (zoneId: number): boolean => {
    return pendingVolumeChanges[zoneId] !== undefined;
  };

  // New volume change handler - only updates pending state, doesn't apply
  const handleVolumeChange = (zoneId: number, volume: number) => {
    // Skip if any modal is open to prevent UI flickering
    if (isAnyModalOpen.current) {
      console.log("Skipping volume change while modal is open");
      return;
    }
    
    const currentZone = zones.find(z => z.id === zoneId);
    if (!currentZone) return;
    
    // Only update pending state, don't apply immediately
    setPendingVolumeChanges(prev => ({
      ...prev,
      [zoneId]: volume
    }));
  };

  // Volume confirmation handler
  const handleVolumeConfirm = (zoneId: number) => {
    const pendingVolume = pendingVolumeChanges[zoneId];
    const currentZone = zones.find(z => z.id === zoneId);
    
    if (pendingVolume === undefined || !currentZone) return;
    
    setVolumeConfirmDialog({
      open: true,
      zoneId,
      newVolume: pendingVolume,
      oldVolume: currentZone.volume
    });
  };

  // Apply confirmed volume change
  const handleVolumeApply = async () => {
    const { zoneId, newVolume } = volumeConfirmDialog;
    if (zoneId === null) return;
    
    try {
      // Apply volume change via API
      const zone = zones.find(z => z.id === zoneId);
      const response = await zoneService.setVolume(zoneId, newVolume, zone?.name);
      
      if (response.success) {
        // Update zones state with new volume
        setZones(zones.map(zone => 
          zone.id === zoneId ? { ...zone, volume: newVolume } : zone
        ));
        
        // Clear pending changes
        setPendingVolumeChanges(prev => {
          const updated = { ...prev };
          delete updated[zoneId];
          return updated;
        });
        
        // Show success message
        enqueueSnackbar(`Volume updated to ${Math.round(newVolume * 100)}%`, { variant: 'success' });
      } else {
        console.warn('Volume set returned unsuccessful:', response.message);
        enqueueSnackbar(`Failed to update volume: ${response.message}`, { variant: 'warning' });
      }
    } catch (error) {
      console.error('Error setting volume:', error);
      enqueueSnackbar('Error updating volume', { variant: 'error' });
    }
    
    // Close dialog
    setVolumeConfirmDialog({ open: false, zoneId: null, newVolume: 0, oldVolume: 0 });
  };

  // Cancel volume change
  const handleVolumeCancel = () => {
    const { zoneId } = volumeConfirmDialog;
    
    if (zoneId !== null) {
      // Clear pending changes for this zone
      setPendingVolumeChanges(prev => {
        const updated = { ...prev };
        delete updated[zoneId];
        return updated;
      });
    }
    
    // Close dialog
    setVolumeConfirmDialog({ open: false, zoneId: null, newVolume: 0, oldVolume: 0 });
  };

  const handleBlockMusic = async (zoneId: number) => {
    try {
      // Get current zone
      const zone = zones.find(z => z.id === zoneId);
      if (!zone) return;
      
      // Set optimistic UI update
      setZones(zones.map(z => 
        z.id === zoneId ? { ...z, blockMusic: !z.blockMusic } : z
      ));
      
      // Call the API
      const response = await zoneService.toggleBlockMusic(zoneId, zone?.name);
      
      if (!response.success) {
        console.error("Error toggling block music:", response.message);
      }
      
      // Fetch only this zone to update
      try {
        const updatedZone = await zoneService.getZoneById(zoneId);
        // Update just this one zone
        setZones(prevZones => 
          prevZones.map(z => z.id === zoneId ? updatedZone : z)
        );
      } catch (err) {
        console.error(`Error fetching updated zone ${zoneId}:`, err);
      }
    } catch (error) {
      console.error('Error toggling block music:', error);
      // Revert UI if there was an error
      const zone = zones.find(z => z.id === zoneId);
      if (zone) {
        try {
          const updatedZone = await zoneService.getZoneById(zoneId);
          setZones(prevZones => 
            prevZones.map(z => z.id === zoneId ? updatedZone : z)
          );
        } catch {
          // Do nothing - to avoid refreshing all zones
        }
      }
    }
  };

  const handleOpenDeleteDialog = (zoneId: number) => {
    setSelectedZoneId(zoneId);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedZoneId(null);
  };

  const handleDeleteZone = async () => {
    if (selectedZoneId) {
      try {
        console.log(`Attempting to delete zone ${selectedZoneId}, user role: ${userRole}, isHotelManager: ${isHotelManager}`);
        
        const zone = zones.find(z => z.id === selectedZoneId);
        const result = await zoneService.deleteZone(selectedZoneId, zone?.name);
        
        if (result.success) {
          console.log(`Zone ${selectedZoneId} deleted successfully`);
          // Remove from local state
          setZones(zones.filter(zone => zone.id !== selectedZoneId));
          handleCloseDeleteDialog();
        } else {
          console.error('Error deleting zone:', result.message);
          alert(`Failed to delete zone: ${result.message || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error in delete zone handler:', error);
        alert(`Failed to delete zone: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
  };

  const handleCloseCreateDialog = () => {
    setOpenCreateDialog(false);
  };

  const handleZoneCreated = () => {
    handleCloseCreateDialog();
    fetchZones();
  };
  
  // Use refs to maintain edit state independent of zone updates
  const selectedZoneRef = useRef<Zone | null>(null);
  
  const handleOpenEditDialog = (zone: Zone, tab: number = 0) => {
    // Create a deep copy of the zone to prevent live updates
    const zoneCopy = JSON.parse(JSON.stringify(zone));

    // Immediately disable auto-refresh to prevent race conditions
    disableAutoRefresh.current = true;

    // Clear any existing progress timers
    Object.values(progressTimers.current).forEach(timer => clearInterval(timer));
    progressTimers.current = {};

    setSelectedZone(zoneCopy);
    selectedZoneRef.current = zoneCopy;
    setSelectedZoneId(zone.id);
    setEditDialogInitialTab(tab);
    setEditDialogOpen(true);
  };
  
  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedZone(null);
    selectedZoneRef.current = null;
    setSelectedZoneId(null);
    
    // Wait a bit longer before re-enabling auto-refresh
    setTimeout(() => {
      disableAutoRefresh.current = false;
      // Timers will be recreated by the useEffect with editDialogOpen dependency
    }, 1000);
  };
  
  const handleZoneUpdated = () => {
    handleCloseEditDialog();
    fetchZones();
  };
  
  const handlePlayPreviousTrack = async (zoneId: number) => {
    try {
      // First fetch current zone to get current track
      const zone = zones.find(z => z.id === zoneId);
      if (!zone) return;
      
      // Remove check if zone is playing - we allow switching tracks when stopped now
      
      console.log(`Playing previous track for zone ${zoneId}`);
      
      // Clear any existing progress data in localStorage for this zone
      localStorage.removeItem(`zoneProgress_${zoneId}`);
      
      // Optimistic UI update - reset progress for better UX
      setZones(prevZones => 
        prevZones.map(z => {
          if (z.id === zoneId && z.currentTrack) {
            return {
              ...z,
              currentTrack: {
                ...z.currentTrack,
                progress: 0
              }
            };
          }
          return z;
        })
      );
      
      const response = await zoneService.playPreviousTrack(zoneId, zone?.name);
      
      if (response.success) {
        // Role-based zone update: HotelUser uses getPlaybackProgress, others use getZoneById
        try {
          if (currentUserRole === UserRole.HotelUser) {
            // For HotelUser: use getPlaybackProgress to avoid 403 errors
            const progress = await zoneService.getPlaybackProgress(zoneId);
            if (progress) {
              setZones(prevZones => 
                prevZones.map(z => {
                  if (z.id === zoneId) {
                    // Update zone state from progress data
                    return {
                      ...z,
                      isPlaying: progress.isPlaying,
                      volume: progress.volume,
                      currentTrack: undefined // Clear track info since we can't predict previous track
                    };
                  }
                  return z;
                })
              );
            }
          } else {
            // For HotelManager: use original getZoneById method
            const updatedZone = await zoneService.getZoneById(zoneId);
            
            // Update only this zone in the list
            setZones(prevZones => 
              prevZones.map(z => {
                if (z.id === zoneId) {
                  // Reset the track ID reference when track changes
                  if (updatedZone.currentTrack && z.currentTrack && 
                      updatedZone.currentTrack.id !== z.currentTrack.id) {
                    lastTrackIdsRef.current[zoneId] = updatedZone.currentTrack.id;
                  }
                  
                  // Ensure progress is reset for the new track
                  return {
                    ...updatedZone,
                    currentTrack: updatedZone.currentTrack ? {
                      ...updatedZone.currentTrack,
                      progress: 0
                    } : undefined
                  };
                }
                return z;
            })
          );
          }
        } catch (updateError) {
          console.error(`Error fetching updated zone ${zoneId}:`, updateError);
        }
      } else {
        console.error('Server returned failure on previous track:', response);
        
        // Show user-friendly notification if message exists
        if (response.message) {
          // Create toast notification instead of alert (you can modify this if you have a toast system)
          console.warn(`Failed to play previous track: ${response.message}`);
          // Show a visually subtle error message without disrupting UX too much
          setZones(prevZones => 
            prevZones.map(z => {
              if (z.id === zoneId) {
                return {
                  ...z,
                  errorMessage: response.message
                };
              }
              return z;
            })
          );
          
          // Auto-clear error message after 3 seconds
          setTimeout(() => {
            setZones(prevZones => 
              prevZones.map(z => {
                if (z.id === zoneId && z.errorMessage === response.message) {
                  const { errorMessage, ...zoneWithoutError } = z;
                  return zoneWithoutError;
                }
                return z;
              })
            );
          }, 3000);
        }
        
        // Fetch fresh state to ensure UI consistency
        setTimeout(async () => {
          try {
            const updatedZone = await zoneService.getZoneById(zoneId);
            setZones(prevZones => 
              prevZones.map(z => z.id === zoneId ? {
                ...updatedZone,
                errorMessage: z.errorMessage // Preserve any error message
              } : z)
            );
          } catch (error) {
            console.error('Failed to refresh zone after error:', error);
          }
        }, 500);
      }
    } catch (error) {
      console.error('Error playing previous track:', error);
      alert('Error playing previous track: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };
  
  const handlePlayNextTrack = async (zoneId: number) => {
    try {
      // First fetch current zone to get current track
      const zone = zones.find(z => z.id === zoneId);
      if (!zone) return;
      
      // Remove check if zone is playing - we allow switching tracks when stopped now
      
      console.log(`Playing next track for zone ${zoneId}`);
      
      // Clear any existing progress data in localStorage for this zone
      localStorage.removeItem(`zoneProgress_${zoneId}`);
      
      // Optimistic UI update - reset progress for better UX
      setZones(prevZones => 
        prevZones.map(z => {
          if (z.id === zoneId && z.currentTrack) {
            return {
              ...z,
              currentTrack: {
                ...z.currentTrack,
                progress: 0
              }
            };
          }
          return z;
        })
      );
      
      // For HotelUser: Get upcoming tracks before the operation to predict next track
      let nextTrack: any = null;
      if (currentUserRole === UserRole.HotelUser) {
        try {
          const upcomingTracks = await zoneService.getUpcomingTracks(zoneId, 1);
          if (upcomingTracks && upcomingTracks.length > 0) {
            const rawTrack = upcomingTracks[0];
            console.log('Next track from upcoming tracks (raw):', rawTrack);
            
            // Parse the string format: "Title_Artist" 
            if (typeof rawTrack === 'string') {
              const parts = rawTrack.split('_');
              const title = parts[0] || 'Unknown Track';
              const artist = parts[1] || 'Unknown Artist';
              
              nextTrack = {
                id: 0, // Temporary ID
                fileName: rawTrack,
                title: title,
                artist: artist,
                key: '',
                duration: '00:00', // Default duration
                progress: 0
              };
            } else {
              // If it's already an object, use as is
              nextTrack = rawTrack;
            }
            console.log('Parsed next track:', nextTrack);
          }
        } catch (error: any) {
          // Ignore upcoming tracks errors, continue with operation
          console.log('Could not fetch upcoming tracks for prediction:', error);
        }
      }

      const response = await zoneService.playNextTrack(zoneId, zone?.name);
      
      if (response.success) {
        // Role-based zone update: HotelUser uses getPlaybackProgress, others use getZoneById
        try {
          if (currentUserRole === UserRole.HotelUser) {
            // For HotelUser: use getPlaybackProgress to avoid 403 errors
            const progress = await zoneService.getPlaybackProgress(zoneId);
            if (progress) {
              setZones(prevZones => 
                prevZones.map(z => {
                  if (z.id === zoneId) {
                    // Update zone state from progress data
                    return {
                      ...z,
                      isPlaying: progress.isPlaying,
                      volume: progress.volume,
                      currentTrack: nextTrack ? {
                        id: nextTrack.id,
                        fileName: nextTrack.fileName || '',
                        title: nextTrack.title,
                        artist: nextTrack.artist,
                        key: nextTrack.key || '',
                        duration: nextTrack.duration,
                        progress: 0
                      } : (z.currentTrack ? {
                        ...z.currentTrack,
                        progress: 0 // Reset progress for new track
                      } : undefined)
                    };
                  }
                  return z;
                })
              );
            }
          } else {
            // For HotelManager: use original getZoneById method
            const updatedZone = await zoneService.getZoneById(zoneId);
            
            // Update only this zone in the list
            setZones(prevZones => 
              prevZones.map(z => {
                if (z.id === zoneId) {
                  // Reset the track ID reference when track changes
                  if (updatedZone.currentTrack && z.currentTrack && 
                      updatedZone.currentTrack.id !== z.currentTrack.id) {
                    lastTrackIdsRef.current[zoneId] = updatedZone.currentTrack.id;
                  }
                  
                  // Ensure progress is reset for the new track
                  return {
                    ...updatedZone,
                    currentTrack: updatedZone.currentTrack ? {
                      ...updatedZone.currentTrack,
                      progress: 0
                    } : undefined
                  };
                }
                return z;
            })
          );
          }
        } catch (nextTrackError) {
          console.error(`Error fetching updated zone ${zoneId}:`, nextTrackError);
        }
      } else {
        console.error('Server returned failure on next track:', response);
        
        // Show user-friendly notification if message exists
        if (response.message) {
          // Create toast notification instead of alert (you can modify this if you have a toast system)
          console.warn(`Failed to play next track: ${response.message}`);
          // Show a visually subtle error message without disrupting UX too much
          setZones(prevZones => 
            prevZones.map(z => {
              if (z.id === zoneId) {
                return {
                  ...z,
                  errorMessage: response.message
                };
              }
              return z;
            })
          );
          
          // Auto-clear error message after 3 seconds
          setTimeout(() => {
            setZones(prevZones => 
              prevZones.map(z => {
                if (z.id === zoneId && z.errorMessage === response.message) {
                  const { errorMessage, ...zoneWithoutError } = z;
                  return zoneWithoutError;
                }
                return z;
              })
            );
          }, 3000);
        }
        
        // Fetch fresh state to ensure UI consistency
        setTimeout(async () => {
          try {
            const updatedZone = await zoneService.getZoneById(zoneId);
            setZones(prevZones => 
              prevZones.map(z => z.id === zoneId ? {
                ...updatedZone,
                errorMessage: z.errorMessage // Preserve any error message
              } : z)
            );
          } catch (error) {
            console.error('Failed to refresh zone after error:', error);
          }
        }, 500);
      }
    } catch (error) {
      console.error('Error playing next track:', error);
      alert('Error playing next track: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Filter zones based on selected category
  const filteredZones = getFilteredZones(zones, selectedCategory);

  return (
    <Box>
      {/* Zone Filter Sidebar */}
      <ZoneFilterSidebar
        selectedCategory={selectedCategory}
        onCategoryChange={setSelectedCategory}
        totalZones={zones.length}
        filteredZones={filteredZones.length}
        zones={zones}
        categories={categories}
        userRole={user?.role}
        isCollapsed={filterCollapsed}
        onToggleCollapse={() => setFilterCollapsed(!filterCollapsed)}
        onManageCategories={() => setCategoryManagementOpen(true)}
      />

      <Grid container  spacing={{ xs: 3, md: 3 }} sx={{ px: { xs: 1, md: 0 }, py: { xs: 2, md: 2 }, justifyContent: { xs: 'center', md: 'flex-start' } }}>
        {filteredZones.length === 0 ? (
          <Grid item  xs={12}>
            <Box sx={{ textAlign: 'center', my: 4 }}>
              <Typography variant="h6" color="text.secondary">
                {selectedCategory ? `No zones found in ${categories.find(cat => cat.id === selectedCategory)?.name || 'selected'} category` : 'No music zones found'}
              </Typography>
              {isHotelManager && (
                <Button
                  startIcon={<AddRoundedIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setOpenCreateDialog(true)}
                  sx={{
                    mt: 2,
                    borderRadius: '12px',
                    px: 3,
                    py: 1,
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: '0.85rem',
                    backgroundColor: '#4294e3',
                    color: '#fff',
                    '&:hover': { backgroundColor: '#3680c9' }
                  }}
                >
                  İlk Zone'u Oluştur
                </Button>
              )}
            </Box>
          </Grid>
        ) : (
          filteredZones.map(zone => (
            <Grid item  xs={12} sm={10} md={6} lg={4} key={zone.id}>
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  bgcolor: zone.blockMusic ? 'rgba(250, 250, 250, 0.8)' : 'background.paper',
                  overflow: 'hidden',
                  borderRadius: 3,
                  boxShadow: zone.isPlaying 
                    ? '0 10px 30px rgba(0, 0, 0, 0.1), 0 0 10px rgba(66, 148, 227, 0.3)' 
                    : '0 5px 15px rgba(0, 0, 0, 0.05)',
                  transition: 'box-shadow 0.3s ease',
                  willChange: 'box-shadow',
                  transform: { xs: 'scale(0.95)', md: 'scale(1)' },
                  mx: { xs: 0.5, md: 0 },
                  '&:hover': {
                    transform: { xs: 'scale(0.98) translate3d(0, -3px, 0)', md: 'translate3d(0, -5px, 0)' },
                    boxShadow: zone.isPlaying 
                      ? '0 15px 35px rgba(0, 0, 0, 0.15), 0 0 15px rgba(66, 148, 227, 0.4)' 
                      : '0 10px 25px rgba(0, 0, 0, 0.1)'
                  }
                }}
              >
                {/* Card Header with gradient */}
                <Box 
                  sx={{ 
                    p: { xs: 1.5, md: 2 }, 
                    background: darkMode
                      ? (zone.isPlaying
                        ? 'linear-gradient(to right, rgba(90, 108, 125, 0.3), rgba(90, 108, 125, 0.2))'
                        : zone.blockMusic
                          ? 'linear-gradient(to right, rgba(60, 64, 67, 0.8), rgba(50, 54, 57, 0.8))'
                          : 'linear-gradient(to right, rgba(60, 64, 67, 0.6), rgba(54, 58, 66, 0.6))')
                      : (zone.isPlaying
                        ? 'linear-gradient(to right, rgba(255, 255, 255, 0.95), rgba(245, 245, 245, 0.95))'
                        : zone.blockMusic
                          ? 'linear-gradient(to right, #e5e5e5, #d4d4d4)'
                          : 'linear-gradient(to right, rgba(255, 255, 255, 0.9), rgba(245, 245, 245, 0.9))'),
                    color: darkMode ? '#e8eaed' : 'text.primary',
                    borderTopLeftRadius: 12,
                    borderTopRightRadius: 12,
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {/* Subtle animation overlay for playing zones */}
                  {zone.isPlaying && (
                    <Box 
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(45deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0) 100%)',
                        backgroundSize: '200% 200%',
                        animation: 'gradient-move 4s ease-in-out infinite',
                        willChange: 'background-position'
                      }}
                    />
                  )}
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
                    <Typography 
                      variant="h6" 
                      component="h2"
                      sx={{ 
                        fontFamily: 'Roboto, "Helvetica", "Arial", sans-serif',
                        fontWeight: 600,
                        textShadow: zone.blockMusic ? 'none' : '0 1px 2px rgba(0,0,0,0.1)'
                      }}
                    >
                      {zone.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Chip
                        label={zone.isPlaying ? 'Playing' : 'Stopped'}
                        color={zone.isPlaying ? 'success' : 'default'}
                        size="small"
                        sx={{
                          mr: 1,
                          fontFamily: 'Heebo, sans-serif',
                          fontWeight: 500,
                          boxShadow: 'none',
                          backgroundColor: zone.isPlaying
                            ? 'rgba(76, 175, 80, 0.15)'
                            : zone.blockMusic
                              ? 'rgba(0,0,0,0.04)'
                              : 'rgba(66, 148, 227, 0.1)',
                          color: zone.isPlaying
                            ? '#2e7d32'
                            : 'text.secondary',
                          backdropFilter: 'blur(8px)',
                          border: zone.isPlaying
                            ? '1px solid rgba(76, 175, 80, 0.25)'
                            : '1px solid rgba(0, 0, 0, 0.06)',
                          '& .MuiChip-label': {
                            px: 1.5
                          }
                        }}
                      />
                      {/* Edit Button */}
                      {isHotelManager && (
                        <IconButton
                          size="small"
                          color="inherit"
                          onClick={() => handleOpenEditDialog(zone)}
                          sx={{
                            ml: 'auto',
                            backgroundColor: 'rgba(255,255,255,0.1)',
                            backdropFilter: 'blur(8px)',
                            border: '1px solid rgba(255,255,255,0.15)',
                            '&:hover': {
                              backgroundColor: 'rgba(255,255,255,0.2)',
                              border: '1px solid rgba(255,255,255,0.3)',
                              transform: 'scale(1.05)',
                            },
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                          }}
                        >
                          <SettingsRoundedIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                </Box>
                
                <CardContent sx={{ flexGrow: 1, pb: 1, pt: { xs: 1.5, md: 2 }, px: { xs: 1.5, md: 2 } }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <DnsRoundedIcon sx={{ color: '#4294e3', mr: 1, fontSize: 16 }} />
                      <Typography color="text.secondary" variant="body2" sx={{ fontFamily: 'Heebo, sans-serif' }}>
                        {zone.deviceName} - {zone.outputChannel}
                      </Typography>
                    </Box>


                  </Box>
                  
                  {zone.blockMusic && (
                    <Chip 
                      icon={<BlockRoundedIcon fontSize="small" />}
                      label="Music Blocked"
                      color="error"
                      size="small"
                      sx={{ 
                        mt: 2,
                        fontFamily: 'Heebo, sans-serif',
                        fontWeight: 500,
                        boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                      }}
                    />
                  )}
                </CardContent>
                
                {/* Current Track Display */}
                <Paper 
                  elevation={0} 
                  sx={{ 
                    mx: { xs: 1.5, md: 2 }, 
                    my: { xs: 1, md: 1.5 }, 
                    p: 0,
                    bgcolor: 'transparent',
                    borderRadius: 2,
                    minHeight: '100px',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    overflow: 'hidden',
                    border: zone.isPlaying ? '1px solid rgba(66, 148, 227, 0.2)' : '1px solid rgba(0, 0, 0, 0.05)'
                  }}
                >
                  {/* Error message display */}
                  {zone.errorMessage && (
                    <Box
                      sx={{
                        p: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        background: 'linear-gradient(45deg, #ffebee, #ffcdd2)',
                        borderBottom: '1px solid rgba(244, 67, 54, 0.1)'
                      }}
                    >
                      <WarningAmberRoundedIcon sx={{
                        color: '#f44336',
                        mr: 1.25,
                        fontSize: 18
                      }} />
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontFamily: 'Roboto, "Helvetica", "Arial", sans-serif',
                          fontWeight: 'bold',
                          color: 'error.main'
                        }}
                      >
                        Error: {zone.errorMessage}
                      </Typography>
                    </Box>
                  )}

                  {/* Track content */}
                  <Box sx={{ p: 1.5, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    {zone.currentTrack ? (
                      <>
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            fontFamily: 'Roboto, "Helvetica", "Arial", sans-serif',
                            fontWeight: 600,
                            fontSize: '1.05rem',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            color: 'text.primary',
                            mb: 0.5
                          }}
                        >
                          {zone.currentTrack.title}
                        </Typography>
                        
                        
                        {/* Track Progress Bar */}
                        <Box sx={{ mt: 'auto' }}>
                          <LinearProgress 
                            variant="determinate" 
                            value={zone.isPlaying ? (zone.currentTrack.progress ?? 0) : 0} 
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              bgcolor: 'rgba(0, 0, 0, 0.05)',
                              willChange: 'auto',
                              '& .MuiLinearProgress-bar': {
                                background: zone.isPlaying 
                                  ? 'linear-gradient(to right, #4294e3, #0062CC, #0078D7)' 
                                  : '#e0e0e0',
                                transition: 'width 0.25s linear',
                                borderRadius: 3,
                                willChange: 'width'
                              }
                            }}
                          />
                          
                          {/* Current Time / Duration display */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75, px: 0.5 }}>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                fontFamily: 'Roboto, "Helvetica", "Arial", sans-serif',
                                fontWeight: 500,
                                color: zone.isPlaying ? 'primary.main' : 'text.secondary', 
                                fontSize: '0.7rem' 
                              }}
                            >
                              {zone.isPlaying 
                                ? formatProgressTime(zone.currentTrack.progress ?? 0, zone.currentTrack.duration)
                                : "00:00"}
                            </Typography>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                fontFamily: 'Roboto, "Helvetica", "Arial", sans-serif',
                                fontWeight: 500,
                                color: 'text.secondary', 
                                fontSize: '0.7rem' 
                              }}
                            >
                              {zone.currentTrack.duration}
                            </Typography>
                          </Box>
                        </Box>
                      </>
                    ) : (
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        height: '80px', 
                        flexDirection: 'column',
                        opacity: 0.7
                      }}>
                        <MusicNoteRoundedIcon sx={{
                          fontSize: 28,
                          color: '#9e9e9e',
                          mb: 1.25
                        }} />
                        <Typography 
                          variant="body2" 
                          sx={{
                            fontFamily: 'Roboto, "Helvetica", "Arial", sans-serif',
                            color: 'text.secondary'
                          }}
                        >
                          No tracks available
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Paper>
                
                {/* Volume Slider with Confirmation */}
                <Box sx={{ 
                  px: { xs: 1.5, md: 2 }, 
                  py: { xs: 1, md: 1.5 },
                  borderTop: '1px solid rgba(0, 0, 0, 0.04)',
                  background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.01), rgba(0, 0, 0, 0))'
                }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box sx={{ 
                      color: zone.blockMusic ? 'text.disabled' : 'text.secondary',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 28,
                      height: 28
                    }}>
                      {getDisplayVolume(zone) > 0 ? (
                        <VolumeUpRoundedIcon sx={{ fontSize: 18 }} />
                      ) : (
                        <VolumeOffRoundedIcon sx={{ fontSize: 18 }} />
                      )}
                    </Box>
                    <Slider
                      size="small"
                      value={getDisplayVolume(zone)}
                      min={0}
                      max={1}
                      step={0.01}
                      onChange={(_, value) => handleVolumeChange(zone.id, value as number)}
                      disabled={zone.blockMusic}
                      sx={{ 
                        '& .MuiSlider-thumb': { 
                          width: 16, 
                          height: 16,
                          boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                          '&:hover, &.Mui-focusVisible': {
                            boxShadow: '0 3px 8px rgba(0,0,0,0.15)'
                          }
                        },
                        '& .MuiSlider-track': { 
                          background: hasPendingVolumeChange(zone.id) 
                            ? 'linear-gradient(to right, #ff9800, #f57c00)' // Orange for pending changes
                            : 'linear-gradient(to right, #4294e3, #0062CC, #0078D7)', // Default blue
                          border: 'none',
                          height: 5 
                        },
                        '& .MuiSlider-rail': { 
                          opacity: 0.3,
                          height: 5 
                        },
                        // Add subtle border for pending changes
                        border: hasPendingVolumeChange(zone.id) ? '1px solid rgba(255, 152, 0, 0.3)' : 'none',
                        borderRadius: '8px',
                        p: hasPendingVolumeChange(zone.id) ? '2px' : '0',
                        transition: 'all 0.2s ease'
                      }}
                    />
                    
                    {/* Volume Confirm Button */}
                    <IconButton
                      size="small"
                      onClick={() => handleVolumeConfirm(zone.id)}
                      disabled={zone.blockMusic || !hasPendingVolumeChange(zone.id)}
                      sx={{
                        width: 28,
                        height: 28,
                        bgcolor: hasPendingVolumeChange(zone.id) ? 'success.main' : 'transparent',
                        color: hasPendingVolumeChange(zone.id) ? 'white' : 'text.disabled',
                        '&:hover': {
                          bgcolor: hasPendingVolumeChange(zone.id) ? 'success.dark' : 'action.hover'
                        },
                        '&.Mui-disabled': {
                          bgcolor: 'transparent',
                          color: 'text.disabled'
                        },
                        transition: 'all 0.2s ease',
                        boxShadow: hasPendingVolumeChange(zone.id) ? '0 2px 4px rgba(76, 175, 80, 0.3)' : 'none'
                      }}
                      title={hasPendingVolumeChange(zone.id) ? "Confirm volume change" : "No pending changes"}
                    >
                      <CheckRoundedIcon sx={{ fontSize: '16px' }} />
                    </IconButton>
                    
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        fontFamily: 'Heebo, sans-serif',
                        fontWeight: 500,
                        color: zone.blockMusic ? 'text.disabled' : (
                          hasPendingVolumeChange(zone.id) ? 'warning.dark' : 'text.secondary'
                        ),
                        width: 32,
                        textAlign: 'center',
                        transition: 'color 0.2s ease'
                      }}
                    >
                      {Math.round(getDisplayVolume(zone) * 100)}%
                    </Typography>
                  </Stack>
                </Box>
                
                {/* Player Controls */}
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  px: { xs: 1.5, md: 2 },
                  pb: { xs: 1.5, md: 2 },
                  pt: { xs: 1, md: 1 },
                  gap: 1,
                  borderTop: '1px solid rgba(0, 0, 0, 0.04)'
                }}>

                  {/* Row 1: Play Controls */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 3, py: 0.5 }}>
                    <IconButton
                      onClick={() => handlePlayPreviousTrack(zone.id)}
                      disabled={zone.blockMusic || !zone.currentTrack}
                      size="small"
                      title="Previous Track"
                      sx={{
                        width: 38,
                        height: 38,
                        backgroundColor: 'rgba(66, 148, 227, 0.12)',
                        border: '1px solid rgba(66, 148, 227, 0.3)',
                        borderRadius: '20px',
                        color: '#4294e3',
                        '&:hover:not(:disabled)': {
                          backgroundColor: 'rgba(66, 148, 227, 0.22)',
                          boxShadow: '0 2px 8px rgba(66, 148, 227, 0.2)',
                          transform: 'translateY(-1px)',
                        },
                        '&.Mui-disabled': {
                          backgroundColor: 'rgba(0,0,0,0.04)',
                          color: 'rgba(0,0,0,0.2)',
                          border: '1px solid rgba(0,0,0,0.06)',
                        },
                        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}
                    >
                      <SkipPreviousRoundedIcon sx={{ fontSize: 20 }} />
                    </IconButton>

                    <Button
                      variant="text"
                      onClick={() => handlePlayStop(zone)}
                      disabled={zone.blockMusic || (!zone.isPlaying && !zone.currentTrack)}
                      title={zone.isPlaying ? "Stop" : "Play"}
                      sx={{
                        minWidth: 100,
                        height: 44,
                        borderRadius: '20px',
                        backgroundColor: zone.isPlaying
                          ? 'rgba(229, 57, 53, 0.10)'
                          : 'rgba(76, 175, 80, 0.10)',
                        border: zone.isPlaying
                          ? '1px solid rgba(229, 57, 53, 0.28)'
                          : '1px solid rgba(76, 175, 80, 0.28)',
                        color: zone.isPlaying ? '#d32f2f' : '#388e3c',
                        fontFamily: '"Inter", sans-serif',
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        fontSize: '14px',
                        letterSpacing: '0.02em',
                        padding: '0 12px',
                        boxShadow: 'none',
                        '&:hover:not(:disabled)': {
                          backgroundColor: zone.isPlaying
                            ? 'rgba(229, 57, 53, 0.18)'
                            : 'rgba(76, 175, 80, 0.18)',
                          boxShadow: zone.isPlaying
                            ? '0 2px 10px rgba(229, 57, 53, 0.2)'
                            : '0 2px 10px rgba(76, 175, 80, 0.2)',
                          transform: 'translateY(-1px)',
                        },
                        '&:active': { transform: 'scale(0.97)' },
                        '&.Mui-disabled': {
                          backgroundColor: 'rgba(0,0,0,0.04)',
                          color: 'rgba(0,0,0,0.2)',
                          border: '1px solid rgba(0,0,0,0.06)',
                          boxShadow: 'none',
                        },
                        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    >
                      {zone.isPlaying ? <StopRoundedIcon sx={{ fontSize: 18, mr: 0.5 }} /> : <PlayArrowRoundedIcon sx={{ fontSize: 18, mr: 0.5 }} />}
                      {zone.isPlaying ? 'Stop' : 'Play'}
                    </Button>

                    <IconButton
                      onClick={() => handlePlayNextTrack(zone.id)}
                      disabled={zone.blockMusic || !zone.currentTrack}
                      size="small"
                      title="Next Track"
                      sx={{
                        width: 38,
                        height: 38,
                        backgroundColor: 'rgba(66, 148, 227, 0.12)',
                        border: '1px solid rgba(66, 148, 227, 0.3)',
                        borderRadius: '20px',
                        color: '#4294e3',
                        '&:hover:not(:disabled)': {
                          backgroundColor: 'rgba(66, 148, 227, 0.22)',
                          boxShadow: '0 2px 8px rgba(66, 148, 227, 0.2)',
                          transform: 'translateY(-1px)',
                        },
                        '&.Mui-disabled': {
                          backgroundColor: 'rgba(0,0,0,0.04)',
                          color: 'rgba(0,0,0,0.2)',
                          border: '1px solid rgba(0,0,0,0.06)',
                        },
                        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}
                    >
                      <SkipNextRoundedIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Box>

                  {/* Row 2: Blocked | Shuffle | Queue | Refresh */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
                    <IconButton
                      onClick={() => {
                        setSelectedHotelId(zone.hotelId);
                        setLoadingBlockedTracks(true);
                        zoneService.getBlockedTracksForZone(zone.id)
                          .then(tracks => {
                            setBlockedTracks(tracks);
                            setBlockedTracksDialogOpen(true);
                          })
                          .catch(error => {
                            console.error("Error fetching blocked tracks:", error);
                            setBlockedTracks([]);
                          })
                          .finally(() => {
                            setLoadingBlockedTracks(false);
                          });
                      }}
                      size="small"
                      title="View Blocked Tracks"
                      sx={{
                        width: 30,
                        height: 30,
                        backgroundColor: 'rgba(66, 148, 227, 0.12)',
                        border: '1px solid rgba(66, 148, 227, 0.3)',
                        borderRadius: '20px',
                        color: '#c62828',
                        '&:hover': {
                          backgroundColor: 'rgba(66, 148, 227, 0.22)',
                          boxShadow: '0 2px 8px rgba(66, 148, 227, 0.2)',
                          transform: 'translateY(-1px)',
                        },
                        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}
                    >
                      <FormatListBulletedRoundedIcon sx={{ fontSize: 14 }} />
                    </IconButton>

                    <IconButton
                      onClick={() => setShuffleConfirm({open: true, zoneId: zone.id, currentShuffle: zone.defaultShuffle})}
                      size="small"
                      title={zone.defaultShuffle ? "Sıralı Moda Geç" : "Karışık Moda Geç"}
                      sx={{
                        width: 30,
                        height: 30,
                        backgroundColor: zone.defaultShuffle ? 'rgba(66, 148, 227, 0.2)' : 'rgba(66, 148, 227, 0.12)',
                        border: zone.defaultShuffle ? '1px solid #4294e3' : '1px solid rgba(66, 148, 227, 0.3)',
                        borderRadius: '20px',
                        color: '#4294e3',
                        '&:hover': {
                          backgroundColor: zone.defaultShuffle ? 'rgba(66, 148, 227, 0.3)' : 'rgba(66, 148, 227, 0.22)',
                          boxShadow: '0 2px 8px rgba(66, 148, 227, 0.2)',
                          transform: 'translateY(-1px)',
                        },
                        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}
                    >
                      {zone.defaultShuffle ? <ShuffleRoundedIcon sx={{ fontSize: 16 }} /> : <FormatListNumberedRoundedIcon sx={{ fontSize: 16 }} />}
                    </IconButton>

                    <IconButton
                      onClick={async () => {
                        setSelectedQueueZoneId(zone.id);
                        setLoadingUpcomingTracks(true);
                        setQueueDialogOpen(true);
                        try {
                          const tracks = await zoneService.getUpcomingTracks(zone.id, 999);
                          setUpcomingTracks(tracks);
                        } catch (error) {
                          console.error("Error fetching upcoming tracks:", error);
                          setUpcomingTracks([]);
                        } finally {
                          setLoadingUpcomingTracks(false);
                        }
                      }}
                      disabled={!zone.currentTrack}
                      size="small"
                      title="Show Upcoming Tracks"
                      sx={{
                        width: 32,
                        height: 32,
                        backgroundColor: 'rgba(66, 148, 227, 0.12)',
                        border: '1px solid rgba(66, 148, 227, 0.3)',
                        borderRadius: '20px',
                        color: '#4294e3',
                        '&:hover:not(:disabled)': {
                          backgroundColor: 'rgba(66, 148, 227, 0.22)',
                          boxShadow: '0 2px 8px rgba(66, 148, 227, 0.2)',
                          transform: 'translateY(-1px)',
                        },
                        '&.Mui-disabled': {
                          backgroundColor: 'rgba(0,0,0,0.04)',
                          color: 'rgba(0,0,0,0.2)',
                          border: '1px solid rgba(0,0,0,0.06)',
                        },
                        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}
                    >
                      <QueueMusicRoundedIcon sx={{ fontSize: 14 }} />
                    </IconButton>

                    <IconButton
                      onClick={() => setRefreshConfirm({open: true, zoneId: zone.id, zoneName: zone.name})}
                      size="small"
                      title="Refresh Playlist"
                      sx={{
                        width: 30,
                        height: 30,
                        backgroundColor: 'rgba(66, 148, 227, 0.12)',
                        border: '1px solid rgba(66, 148, 227, 0.3)',
                        borderRadius: '20px',
                        color: '#4294e3',
                        '&:hover': {
                          backgroundColor: 'rgba(66, 148, 227, 0.22)',
                          boxShadow: '0 2px 8px rgba(66, 148, 227, 0.2)',
                          transform: 'rotate(90deg)',
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}
                    >
                      <SyncRoundedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>

                  {/* Row 3: Block | Events */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
                    <Button
                      variant="text"
                      title="Çalan parçayı bloklayıp sonraki parçaya geç"
                      onClick={() => {
                        if (zone.currentTrack) {
                          setBlockConfirm({open: true, zoneId: zone.id, zoneName: zone.name, trackTitle: zone.currentTrack.title});
                        } else {
                          enqueueSnackbar("Bloklanacak çalan bir parça yok", { variant: 'warning', autoHideDuration: 3000 });
                        }
                      }}
                      disabled={!zone.currentTrack}
                      size="small"
                      sx={{
                        minWidth: 90,
                        height: 32,
                        borderRadius: '20px',
                        fontFamily: '"Inter", sans-serif',
                        fontWeight: 500,
                        fontSize: '11.5px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.02em',
                        backgroundColor: 'rgba(229, 57, 53, 0.07)',
                        border: '1px solid rgba(229, 57, 53, 0.2)',
                        color: '#c62828',
                        padding: '0 10px',
                        boxShadow: '0 0 8px rgba(229,57,53,0.2)',
                        '&:hover:not(:disabled)': {
                          backgroundColor: 'rgba(229, 57, 53, 0.14)',
                          boxShadow: '0 0 12px rgba(229,57,53,0.3)',
                          transform: 'translateY(-1px)',
                        },
                        '&:active': { transform: 'scale(0.97)' },
                        '&.Mui-disabled': {
                          backgroundColor: 'rgba(0,0,0,0.04)',
                          color: 'rgba(0,0,0,0.2)',
                          border: '1px solid rgba(0,0,0,0.06)',
                          boxShadow: 'none',
                        },
                        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    >
                      <SecurityRoundedIcon sx={{ fontSize: 14, mr: 0.5 }} />
                      Block
                    </Button>

                    {isHotelManager && (
                    <Button
                      variant="text"
                      onClick={() => handleOpenEditDialog(zone, 1)}
                      size="small"
                      title="Manage Events for this Zone"
                      sx={{
                        minWidth: 90,
                        height: 32,
                        borderRadius: '20px',
                        fontFamily: '"Inter", sans-serif',
                        fontWeight: 500,
                        fontSize: '11.5px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.02em',
                        backgroundColor: 'rgba(66, 148, 227, 0.12)',
                        border: '1px solid rgba(66, 148, 227, 0.3)',
                        color: '#4294e3',
                        padding: '0 10px',
                        boxShadow: 'none',
                        '&:hover': {
                          backgroundColor: 'rgba(66, 148, 227, 0.22)',
                          boxShadow: '0 2px 8px rgba(66, 148, 227, 0.2)',
                          transform: 'translateY(-1px)',
                        },
                        '&:active': { transform: 'scale(0.97)' },
                        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    >
                      <EventRoundedIcon sx={{ fontSize: 14, mr: 0.5 }} />
                      Events
                    </Button>
                    )}
                  </Box>

                  {/* Row 4: Delete */}
                  {isHotelManager && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                    <IconButton
                      onClick={() => handleOpenDeleteDialog(zone.id)}
                      size="small"
                      title="Delete Zone"
                      sx={{
                        width: 30,
                        height: 30,
                        backgroundColor: 'rgba(66, 148, 227, 0.12)',
                        border: '1px solid rgba(66, 148, 227, 0.3)',
                        borderRadius: '20px',
                        color: '#4294e3',
                        '&:hover': {
                          backgroundColor: 'rgba(244, 67, 54, 0.12)',
                          color: '#e53935',
                          border: '1px solid rgba(229, 57, 53, 0.3)',
                          boxShadow: '0 2px 8px rgba(229, 57, 53, 0.15)',
                          transform: 'scale(1.08)',
                        },
                        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}
                    >
                      <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                  )}
                </Box>
              </Card>
            </Grid>
          ))
        )}
      </Grid>

      {/* Create Zone Dialog */}
      <Dialog
        open={openCreateDialog}
        onClose={handleCloseCreateDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.12)',
            overflow: 'hidden',
            maxHeight: '85vh'
          }
        }}
      >
        {/* Header */}
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#fff',
          borderBottom: '1px solid #f0f0f0',
          py: 2,
          px: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: '10px',
              backgroundColor: 'rgba(66, 148, 227, 0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <AddRoundedIcon sx={{ fontSize: 18, color: '#4294e3' }} />
            </Box>
            <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: '#1a1a2e' }}>
              Yeni Zone Oluştur
            </Typography>
          </Box>
          <IconButton
            onClick={handleCloseCreateDialog}
            size="small"
            sx={{
              width: 32, height: 32,
              color: '#999',
              '&:hover': { color: '#666', backgroundColor: 'rgba(0,0,0,0.04)' }
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 1 }}>
          <ZoneForm
            onSuccess={handleZoneCreated}
            hotelId={user?.hotelId}
            categories={categories.map(cat => ({
              id: cat.id,
              name: cat.name,
              icon: cat.icon,
              color: cat.color,
              description: cat.description,
              hotelId: user?.hotelId || 0,
              isActive: true,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              zoneCount: 0
            }))}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        PaperProps={{ sx: { borderRadius: '16px', maxWidth: 380, mx: 'auto', overflow: 'hidden' } }}
      >
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: '14px', mx: 'auto', mb: 2,
            backgroundColor: 'rgba(229, 57, 53, 0.08)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <DeleteOutlineRoundedIcon sx={{ fontSize: 24, color: '#c62828' }} />
          </Box>
          <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: '#1a1a2e', mb: 0.5 }}>
            Zone'u Sil
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', color: '#888', mb: 2.5, lineHeight: 1.5 }}>
            Bu zone'u silmek istediğinizden emin misiniz?
            <br />Bu işlem <strong style={{color: '#1a1a2e'}}>geri alınamaz</strong>.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              fullWidth
              onClick={handleCloseDeleteDialog}
              sx={{
                textTransform: 'none', fontWeight: 500, borderRadius: '10px',
                color: '#666', backgroundColor: '#f5f5f5', py: 1,
                '&:hover': { backgroundColor: '#eee' }
              }}
            >
              Vazgeç
            </Button>
            <Button
              fullWidth
              onClick={handleDeleteZone}
              sx={{
                textTransform: 'none', fontWeight: 600, borderRadius: '10px',
                backgroundColor: '#c62828', color: '#fff', py: 1,
                '&:hover': { backgroundColor: '#b71c1c' }
              }}
            >
              Sil
            </Button>
          </Box>
        </Box>
      </Dialog>
      
      {/* Edit Zone Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.12)',
            overflow: 'hidden',
            maxHeight: '85vh'
          }
        }}
      >
        {/* Header */}
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#fff',
          borderBottom: '1px solid #f0f0f0',
          py: 2,
          px: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: '10px',
              backgroundColor: 'rgba(66, 148, 227, 0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <SettingsRoundedIcon sx={{ fontSize: 18, color: '#4294e3' }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: '#1a1a2e', lineHeight: 1.3 }}>
                Zone Ayarları
              </Typography>
              {selectedZone && (
                <Typography sx={{ fontSize: '0.75rem', color: '#888', lineHeight: 1.3 }}>
                  {selectedZone.name}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton
            onClick={handleCloseEditDialog}
            size="small"
            sx={{
              width: 32, height: 32,
              color: '#999',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' }
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>

        {/* Content */}
        <DialogContent sx={{
          p: 3, pt: 2,
          overflow: 'auto',
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.15)',
            borderRadius: '3px',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.25)' }
          },
          '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(0,0,0,0.15) transparent',
        }}>
          {/* Use the initial selectedZone value from the ref to prevent refreshing */}
          {(selectedZoneRef.current || selectedZone) && (
            <ZoneForm
              onSuccess={handleZoneUpdated}
              hotelId={(selectedZoneRef.current || selectedZone)!.hotelId}
              initialData={{
                name: (selectedZoneRef.current || selectedZone)!.name,
                deviceName: (selectedZoneRef.current || selectedZone)!.deviceName,
                outputChannel: (selectedZoneRef.current || selectedZone)!.outputChannel,
                outputChannels: (selectedZoneRef.current || selectedZone)!.outputChannels,
                channelConfiguration: (selectedZoneRef.current || selectedZone)!.channelConfiguration,
                musicFolderPath: (selectedZoneRef.current || selectedZone)!.musicFolderPath,
                musicFolderPaths: (selectedZoneRef.current || selectedZone)!.musicFolderPaths,
                hotelId: (selectedZoneRef.current || selectedZone)!.hotelId,
                categoryId: (selectedZoneRef.current || selectedZone)!.categoryId,
              }}
              isEditing={true}
              zoneId={(selectedZoneRef.current || selectedZone)!.id}
              initialTab={editDialogInitialTab}
              categories={categories.map(cat => ({
                id: cat.id,
                name: cat.name,
                icon: cat.icon,
                color: cat.color,
                description: cat.description,
                hotelId: user?.hotelId || 0,
                isActive: true,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                zoneCount: 0
              }))}
            />
          )}
        </DialogContent>

        {/* Footer */}
        <DialogActions sx={{
          px: 3, py: 1.5,
          borderTop: '1px solid #f0f0f0',
          justifyContent: 'center'
        }}>
          <Button
            onClick={handleCloseEditDialog}
            sx={{
              textTransform: 'none', fontWeight: 500, borderRadius: '10px',
              color: '#666', backgroundColor: '#f5f5f5', px: 4, py: 0.8,
              fontSize: '0.85rem',
              '&:hover': { backgroundColor: '#eee' }
            }}
          >
            Kapat
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Blocked Tracks Dialog */}
      <Dialog
        open={blockedTracksDialogOpen}
        onClose={() => setBlockedTracksDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.12)',
            overflow: 'hidden',
            maxHeight: '80vh'
          }
        }}
      >
        {/* Header */}
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#fff',
          borderBottom: '1px solid #f0f0f0',
          py: 2,
          px: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: '10px',
              backgroundColor: 'rgba(229, 57, 53, 0.08)',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <BlockRoundedIcon sx={{ fontSize: 18, color: '#c62828' }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: '#1a1a2e', lineHeight: 1.3 }}>
                Bloklanmış Parçalar
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: '#888', lineHeight: 1.3 }}>
                {blockedTracks.length > 0 ? `${blockedTracks.length} parça bloklu` : 'Henüz bloklanmış parça yok'}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={() => setBlockedTracksDialogOpen(false)}
            size="small"
            sx={{
              width: 32, height: 32,
              color: '#999',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' }
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>

        {/* Content */}
        <DialogContent sx={{
          p: 0,
          overflow: 'auto',
          maxHeight: '60vh',
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.15)',
            borderRadius: '3px',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.25)' }
          },
          '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(0,0,0,0.15) transparent',
        }}>
          {/* Loading */}
          {loadingBlockedTracks && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', py: 6, gap: 1.5 }}>
              <CircularProgress size={28} sx={{ color: '#c62828' }} />
              <Typography sx={{ fontSize: '0.8rem', color: '#999' }}>Yükleniyor...</Typography>
            </Box>
          )}

          {/* Track List */}
          {!loadingBlockedTracks && blockedTracks.length > 0 && (
            <Box sx={{ py: 1 }}>
              {blockedTracks.map((track, index) => (
                <Box
                  key={track.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 2.5,
                    py: 1.5,
                    borderBottom: index < blockedTracks.length - 1 ? '1px solid #f5f5f5' : 'none',
                    transition: 'background-color 0.15s',
                    '&:hover': { backgroundColor: '#fafafa' }
                  }}
                >
                  {/* Index number */}
                  <Typography sx={{
                    fontSize: '0.7rem', fontWeight: 600, color: '#ccc',
                    minWidth: 20, textAlign: 'center'
                  }}>
                    {index + 1}
                  </Typography>

                  {/* Track info */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{
                      fontSize: '0.85rem', fontWeight: 600, color: '#1a1a2e',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      lineHeight: 1.4
                    }}>
                      {track.title}
                    </Typography>
                    <Typography sx={{
                      fontSize: '0.75rem', color: '#888', lineHeight: 1.4,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                    }}>
                      {[track.artist, track.album].filter(Boolean).join(' — ')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.3 }}>
                      <Typography sx={{ fontSize: '0.65rem', color: '#aaa', display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        <AccessTimeRoundedIcon sx={{ fontSize: 11 }} />
                        {new Date(track.blockedAt).toLocaleString('tr-TR', {
                          day: 'numeric', month: 'short', year: 'numeric',
                          hour: '2-digit', minute: '2-digit'
                        })}
                      </Typography>
                      {track.blockedByUsername && (
                        <Typography sx={{ fontSize: '0.65rem', color: '#aaa', display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <PersonRoundedIcon sx={{ fontSize: 11 }} />
                          {track.blockedByUsername}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* Unblock button */}
                  <IconButton
                    size="small"
                    title="Bloku Kaldır"
                    onClick={() => {
                      if (window.confirm(`Bu parçanın blokunu kaldırmak istediğinize emin misiniz?\n"${track.title}"`)) {
                        zoneService.unblockTrack(track.id)
                          .then(response => {
                            if (response.success) {
                              setBlockedTracks(prevTracks => prevTracks.filter(t => t.id !== track.id));
                              enqueueSnackbar(`"${track.title}" parçasının bloku kaldırıldı`, { variant: 'success', autoHideDuration: 3000 });
                            } else {
                              enqueueSnackbar(response.message || "Blok kaldırma işlemi başarısız", { variant: 'error', autoHideDuration: 3000 });
                            }
                          })
                          .catch(error => {
                            console.error("Error unblocking track:", error);
                            enqueueSnackbar("Blok kaldırma sırasında hata oluştu.", { variant: 'error', autoHideDuration: 3000 });
                          });
                      }
                    }}
                    sx={{
                      width: 32, height: 32,
                      backgroundColor: 'rgba(229, 57, 53, 0.06)',
                      border: '1px solid rgba(229, 57, 53, 0.15)',
                      borderRadius: '8px',
                      color: '#c62828',
                      flexShrink: 0,
                      '&:hover': {
                        backgroundColor: 'rgba(229, 57, 53, 0.12)',
                        border: '1px solid rgba(229, 57, 53, 0.3)',
                      },
                      transition: 'all 0.2s'
                    }}
                  >
                    <LockOpenRoundedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

          {/* Empty state */}
          {!loadingBlockedTracks && blockedTracks.length === 0 && (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <BlockRoundedIcon sx={{ fontSize: 40, color: 'rgba(0,0,0,0.1)', mb: 1.5 }} />
              <Typography sx={{ fontSize: '0.9rem', color: '#999', mb: 0.5 }}>
                Bloklanmış parça yok
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: '#bbb' }}>
                Block butonuyla çalan parçayı bloklayabilirsiniz
              </Typography>
            </Box>
          )}
        </DialogContent>

        {/* Footer */}
        <DialogActions sx={{
          px: 3, py: 1.5,
          borderTop: '1px solid #f0f0f0',
          justifyContent: 'flex-end'
        }}>
          <Button
            onClick={() => setBlockedTracksDialogOpen(false)}
            variant="text"
            sx={{
              textTransform: 'none', fontWeight: 500, fontSize: '0.85rem',
              borderRadius: '8px', px: 2.5, color: '#666',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
            }}
          >
            Kapat
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Block Confirmation Dialog */}
      <Dialog
        open={blockConfirm.open}
        onClose={() => setBlockConfirm(prev => ({...prev, open: false}))}
        PaperProps={{ sx: { borderRadius: '16px', maxWidth: 380, mx: 'auto', overflow: 'hidden' } }}
      >
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: '14px', mx: 'auto', mb: 2,
            backgroundColor: 'rgba(229, 57, 53, 0.08)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <SecurityRoundedIcon sx={{ fontSize: 24, color: '#c62828' }} />
          </Box>
          <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: '#1a1a2e', mb: 0.5 }}>
            Parçayı Blokla
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', color: '#888', mb: 2.5, lineHeight: 1.5 }}>
            <strong style={{color: '#1a1a2e'}}>"{blockConfirm.trackTitle}"</strong> parçasını bloklamak istiyor musunuz?
            <br />Bu parça artık çalma listesinde atlanacaktır.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              fullWidth
              onClick={() => setBlockConfirm(prev => ({...prev, open: false}))}
              sx={{
                textTransform: 'none', fontWeight: 500, borderRadius: '10px',
                color: '#666', backgroundColor: '#f5f5f5', py: 1,
                '&:hover': { backgroundColor: '#eee' }
              }}
            >
              Vazgeç
            </Button>
            <Button
              fullWidth
              onClick={() => {
                setBlockConfirm(prev => ({...prev, open: false}));
                if (blockConfirm.zoneId !== null) {
                  zoneService.blockCurrentTrack(blockConfirm.zoneId, blockConfirm.zoneName)
                    .then(response => {
                      if (response.success) {
                        enqueueSnackbar(`"${blockConfirm.trackTitle}" parçası bloklandı`, { variant: 'success', autoHideDuration: 3000 });
                        refreshZone(blockConfirm.zoneId!);
                      } else {
                        enqueueSnackbar(response.message?.toString() || "Parça bloklanamadı", { variant: 'error', autoHideDuration: 3000 });
                      }
                    })
                    .catch(() => {
                      enqueueSnackbar("Parça bloklama sırasında hata oluştu.", { variant: 'error', autoHideDuration: 3000 });
                    });
                }
              }}
              sx={{
                textTransform: 'none', fontWeight: 600, borderRadius: '10px',
                backgroundColor: '#c62828', color: '#fff', py: 1,
                '&:hover': { backgroundColor: '#b71c1c' }
              }}
            >
              Blokla
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Shuffle Confirmation Dialog */}
      <Dialog
        open={shuffleConfirm.open}
        onClose={() => setShuffleConfirm(prev => ({...prev, open: false}))}
        PaperProps={{ sx: { borderRadius: '16px', maxWidth: 380, mx: 'auto', overflow: 'hidden' } }}
      >
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: '14px', mx: 'auto', mb: 2,
            backgroundColor: 'rgba(66, 148, 227, 0.1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            {shuffleConfirm.currentShuffle
              ? <FormatListNumberedRoundedIcon sx={{ fontSize: 24, color: '#4294e3' }} />
              : <ShuffleRoundedIcon sx={{ fontSize: 24, color: '#4294e3' }} />
            }
          </Box>
          <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: '#1a1a2e', mb: 0.5 }}>
            {shuffleConfirm.currentShuffle ? 'Sıralı Çalma' : 'Karışık Çalma'}
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', color: '#888', mb: 2.5, lineHeight: 1.5 }}>
            {shuffleConfirm.currentShuffle
              ? 'Listeyi sıralı olarak çalmak istiyor musunuz?'
              : 'Listeyi karışık olarak çalmak istiyor musunuz?'
            }
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              fullWidth
              onClick={() => setShuffleConfirm(prev => ({...prev, open: false}))}
              sx={{
                textTransform: 'none', fontWeight: 500, borderRadius: '10px',
                color: '#666', backgroundColor: '#f5f5f5', py: 1,
                '&:hover': { backgroundColor: '#eee' }
              }}
            >
              Vazgeç
            </Button>
            <Button
              fullWidth
              onClick={async () => {
                setShuffleConfirm(prev => ({...prev, open: false}));
                if (shuffleConfirm.zoneId !== null) {
                  const newShuffle = !shuffleConfirm.currentShuffle;
                  const result = await zoneService.setShuffle(shuffleConfirm.zoneId, newShuffle);
                  if (result.success) {
                    setZones(prev => prev.map(z => z.id === shuffleConfirm.zoneId ? { ...z, defaultShuffle: newShuffle } : z));
                    enqueueSnackbar(newShuffle ? 'Karışık çalma aktif' : 'Sıralı çalma aktif', { variant: 'success', autoHideDuration: 2000 });
                  }
                }
              }}
              sx={{
                textTransform: 'none', fontWeight: 600, borderRadius: '10px',
                backgroundColor: '#4294e3', color: '#fff', py: 1,
                '&:hover': { backgroundColor: '#3680c9' }
              }}
            >
              {shuffleConfirm.currentShuffle ? 'Sıralı Çal' : 'Karışık Çal'}
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Refresh Playlist Confirmation Dialog */}
      <Dialog
        open={refreshConfirm.open}
        onClose={() => setRefreshConfirm(prev => ({...prev, open: false}))}
        PaperProps={{ sx: { borderRadius: '16px', maxWidth: 380, mx: 'auto', overflow: 'hidden' } }}
      >
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: '14px', mx: 'auto', mb: 2,
            backgroundColor: 'rgba(66, 148, 227, 0.1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <SyncRoundedIcon sx={{ fontSize: 24, color: '#4294e3' }} />
          </Box>
          <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: '#1a1a2e', mb: 0.5 }}>
            Playlisti Yenile
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', color: '#888', mb: 2.5, lineHeight: 1.5 }}>
            Playlisti yeniden oluşturmak istediğinizden emin misiniz?
            <br />Mevcut sıralama sıfırlanacaktır.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              fullWidth
              onClick={() => setRefreshConfirm(prev => ({...prev, open: false}))}
              sx={{
                textTransform: 'none', fontWeight: 500, borderRadius: '10px',
                color: '#666', backgroundColor: '#f5f5f5', py: 1,
                '&:hover': { backgroundColor: '#eee' }
              }}
            >
              Vazgeç
            </Button>
            <Button
              fullWidth
              onClick={() => {
                setRefreshConfirm(prev => ({...prev, open: false}));
                if (refreshConfirm.zoneId !== null) {
                  handleRefreshPlaylist(refreshConfirm.zoneId);
                  enqueueSnackbar('Playlist yenileniyor...', { variant: 'info', autoHideDuration: 2000 });
                }
              }}
              sx={{
                textTransform: 'none', fontWeight: 600, borderRadius: '10px',
                backgroundColor: '#4294e3', color: '#fff', py: 1,
                '&:hover': { backgroundColor: '#3680c9' }
              }}
            >
              Yenile
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Upcoming Tracks Dialog */}
      <Dialog
        open={queueDialogOpen}
        onClose={() => setQueueDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.12)',
            overflow: 'hidden',
            maxHeight: '80vh'
          }
        }}
      >
        {/* Header */}
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#fff',
          borderBottom: '1px solid #f0f0f0',
          py: 2,
          px: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: '10px',
              backgroundColor: 'rgba(66, 148, 227, 0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <QueueMusicRoundedIcon sx={{ fontSize: 18, color: '#4294e3' }} />
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: '#1a1a2e', lineHeight: 1.3 }}>
                  Çalma Listesi
                </Typography>
                {(() => {
                  const zone = zones.find(z => z.id === selectedQueueZoneId);
                  if (zone?.currentScheduleName) {
                    return (
                      <Box sx={{
                        px: 1, py: 0.2, borderRadius: '6px',
                        fontSize: '0.65rem', fontWeight: 600,
                        backgroundColor: 'rgba(76, 175, 80, 0.1)',
                        color: '#4caf50',
                        border: '1px solid rgba(76, 175, 80, 0.2)',
                        whiteSpace: 'nowrap', lineHeight: 1.4
                      }}>
                        {zone.currentScheduleName}
                      </Box>
                    );
                  }
                  return (
                    <Box sx={{
                      px: 1, py: 0.2, borderRadius: '6px',
                      fontSize: '0.65rem', fontWeight: 600,
                      backgroundColor: 'rgba(158, 158, 158, 0.08)',
                      color: '#9e9e9e',
                      border: '1px solid rgba(158, 158, 158, 0.15)',
                      whiteSpace: 'nowrap', lineHeight: 1.4
                    }}>
                      Zone Klasörleri
                    </Box>
                  );
                })()}
              </Box>
              <Typography sx={{ fontSize: '0.75rem', color: '#888', lineHeight: 1.3 }}>
                {loadingUpcomingTracks
                  ? 'Yükleniyor...'
                  : (() => {
                      const currentTrack = zones.find(z => z.id === selectedQueueZoneId)?.currentTrack;
                      const totalCount = upcomingTracks.filter(t => typeof t !== 'string' || !(t.startsWith('---') || t.includes('başa dönecek') || t.includes('tekrar'))).length + (currentTrack ? 1 : 0);
                      return totalCount > 0 ? `${totalCount} parça` : 'Parça bulunamadı';
                    })()
                }
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={() => setQueueDialogOpen(false)}
            size="small"
            sx={{
              width: 32, height: 32,
              color: '#999',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' }
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>

        {/* Content */}
        <DialogContent sx={{
          p: 0,
          overflow: 'auto',
          maxHeight: '60vh',
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.15)',
            borderRadius: '3px',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.25)' }
          },
          '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(0,0,0,0.15) transparent',
        }}>
          {/* Loading */}
          {loadingUpcomingTracks && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', py: 6, gap: 1.5 }}>
              <CircularProgress size={28} sx={{ color: '#4294e3' }} />
              <Typography sx={{ fontSize: '0.8rem', color: '#999' }}>Yükleniyor...</Typography>
            </Box>
          )}

          {/* Current Track */}
          {!loadingUpcomingTracks && selectedQueueZoneId && zones.find(z => z.id === selectedQueueZoneId)?.currentTrack && (
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1.5,
              px: 2.5, py: 1.5,
              backgroundColor: 'rgba(66, 148, 227, 0.06)',
              borderBottom: '1px solid #f0f0f0'
            }}>
              <Box sx={{
                width: 28, height: 28, borderRadius: '8px',
                backgroundColor: '#4294e3',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0
              }}>
                {zones.find(z => z.id === selectedQueueZoneId)?.isPlaying
                  ? <PlayArrowRoundedIcon sx={{ fontSize: 16, color: '#fff' }} />
                  : <MusicNoteRoundedIcon sx={{ fontSize: 14, color: '#fff' }} />
                }
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{
                  fontSize: '0.85rem', fontWeight: 600, color: '#1a1a2e',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.4
                }}>
                  {zones.find(z => z.id === selectedQueueZoneId)?.currentTrack?.title}
                </Typography>
                <Typography sx={{
                  fontSize: '0.75rem', color: '#888', lineHeight: 1.4,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                }}>
                  {zones.find(z => z.id === selectedQueueZoneId)?.currentTrack?.artist !== 'Unknown'
                    ? zones.find(z => z.id === selectedQueueZoneId)?.currentTrack?.artist
                    : ''}
                  {zones.find(z => z.id === selectedQueueZoneId)?.currentTrack?.duration && (
                    <>{zones.find(z => z.id === selectedQueueZoneId)?.currentTrack?.artist !== 'Unknown' ? ' — ' : ''}{zones.find(z => z.id === selectedQueueZoneId)?.currentTrack?.duration}</>
                  )}
                </Typography>
              </Box>
              <Box sx={{
                px: 1, py: 0.3, borderRadius: '6px',
                fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.5px',
                backgroundColor: '#4294e3', color: '#fff',
                flexShrink: 0
              }}>
                NOW
              </Box>
            </Box>
          )}

          {/* Track List */}
          {!loadingUpcomingTracks && upcomingTracks.length > 0 && (
            <Box sx={{ py: 0.5 }}>
              {upcomingTracks.map((track, index) => {
                // Check if this is a separator message
                const isSeparator = typeof track === 'string' && (
                  track.startsWith('---') ||
                  track.includes('başa dönecek') ||
                  track.includes('tekrar')
                );

                if (isSeparator) {
                  return (
                    <Box
                      key={`separator-${index}`}
                      sx={{
                        py: 1, px: 2.5, mx: 1.5, my: 0.5,
                        textAlign: 'center',
                        backgroundColor: 'rgba(255, 165, 0, 0.05)',
                        borderRadius: '8px',
                        border: '1px solid rgba(255, 165, 0, 0.15)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5
                      }}
                    >
                      <SyncRoundedIcon sx={{ fontSize: 14, color: 'rgba(255, 165, 0, 0.7)' }} />
                      <Typography sx={{ fontSize: '0.7rem', color: '#aaa', fontWeight: 500 }}>
                        {typeof track === 'string' ? track.replace(/---/g, '') : ''}
                      </Typography>
                    </Box>
                  );
                }

                // Get file name without path and extension for display
                const fileName = typeof track === 'string'
                  ? track.split('/').pop()?.split('\\').pop()?.replace(/\.[^/.]+$/, '')
                  : track.title || track;

                return (
                  <Box
                    key={`track-${index}`}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1.5,
                      px: 2.5, py: 1.2,
                      borderBottom: '1px solid #f5f5f5',
                      transition: 'background-color 0.15s',
                      '&:hover': { backgroundColor: '#fafafa' }
                    }}
                  >
                    {/* Index number */}
                    <Typography sx={{
                      fontSize: '0.7rem', fontWeight: 600,
                      color: index === 0 ? '#4294e3' : '#ccc',
                      minWidth: 20, textAlign: 'center'
                    }}>
                      {index + 1}
                    </Typography>

                    {/* Track icon */}
                    {index === 0
                      ? <SkipNextRoundedIcon sx={{ fontSize: 16, color: '#4294e3', flexShrink: 0 }} />
                      : <MusicNoteRoundedIcon sx={{ fontSize: 14, color: '#ccc', flexShrink: 0 }} />
                    }

                    {/* Track name */}
                    <Typography sx={{
                      fontSize: '0.85rem',
                      fontWeight: index === 0 ? 600 : 400,
                      color: index === 0 ? '#4294e3' : '#1a1a2e',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      flex: 1, minWidth: 0
                    }}>
                      {fileName}
                    </Typography>

                    {/* NEXT badge */}
                    {index === 0 && (
                      <Box sx={{
                        px: 0.8, py: 0.2, borderRadius: '4px',
                        fontSize: '0.55rem', fontWeight: 700, letterSpacing: '0.5px',
                        backgroundColor: 'rgba(66, 148, 227, 0.1)', color: '#4294e3',
                        flexShrink: 0
                      }}>
                        NEXT
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}

          {/* Empty state */}
          {!loadingUpcomingTracks && upcomingTracks.length === 0 && (
            <Box sx={{ py: 6, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
              <MusicNoteRoundedIcon sx={{ fontSize: 32, color: '#ddd' }} />
              <Typography sx={{ fontSize: '0.85rem', color: '#999' }}>
                Çalma listesinde parça bulunamadı
              </Typography>
            </Box>
          )}
        </DialogContent>

        {/* Footer */}
        <DialogActions sx={{
          px: 3, py: 1.5,
          borderTop: '1px solid #f0f0f0',
          justifyContent: 'center'
        }}>
          <Button
            onClick={() => setQueueDialogOpen(false)}
            sx={{
              textTransform: 'none', fontWeight: 500, borderRadius: '10px',
              color: '#666', backgroundColor: '#f5f5f5', px: 4, py: 0.8,
              fontSize: '0.85rem',
              '&:hover': { backgroundColor: '#eee' }
            }}
          >
            Kapat
          </Button>
        </DialogActions>
      </Dialog>

      {/* Volume Confirmation Dialog */}
      <Dialog
        open={volumeConfirmDialog.open}
        onClose={handleVolumeCancel}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            minWidth: '320px',
            maxWidth: '400px',
            backgroundColor: darkMode ? '#363a42' : '#ffffff',
            border: darkMode ? '1px solid rgba(255, 255, 255, 0.08)' : 'none'
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          fontFamily: darkMode ? 'sans-serif' : 'Inter, sans-serif',
          fontWeight: 600,
          fontSize: '1.1rem',
          color: darkMode ? '#e8eaed' : 'text.primary',
          backgroundColor: darkMode ? '#363a42' : 'inherit'
        }}>
          <VolumeUpRoundedIcon color="primary" />
          Volume Change Confirmation
        </DialogTitle>
        <DialogContent sx={{ 
          p: 3, 
          pt: 1,
          backgroundColor: darkMode ? '#363a42' : 'inherit'
        }}>
          <DialogContentText sx={{ 
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            color: darkMode ? '#e8eaed' : 'text.primary',
            fontFamily: darkMode ? 'sans-serif' : 'Inter, sans-serif'
          }}>
            <Box>
              Are you sure you want to change the volume?
            </Box>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              bgcolor: darkMode ? '#2f3239' : 'grey.50',
              borderRadius: '8px',
              border: darkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)'
            }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Current
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {Math.round(volumeConfirmDialog.oldVolume * 100)}%
                </Typography>
              </Box>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                color: 'primary.main'
              }}>
                →
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  New
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {Math.round(volumeConfirmDialog.newVolume * 100)}%
                </Typography>
              </Box>
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ 
          px: 3, 
          py: 2,
          borderTop: darkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.06)',
          background: darkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
          backgroundColor: darkMode ? '#363a42' : 'inherit',
          justifyContent: 'space-between'
        }}>
          <Button 
            onClick={handleVolumeCancel}
            variant="outlined"
            sx={{ 
              fontFamily: darkMode ? 'sans-serif' : 'Inter, sans-serif',
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: '8px',
              px: 3
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleVolumeApply}
            variant="contained"
            color="success"
            sx={{ 
              fontFamily: darkMode ? 'sans-serif' : 'Inter, sans-serif',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '8px',
              px: 3,
              boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)'
            }}
            startIcon={<CheckRoundedIcon />}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category Management Modal */}
      <CategoryManagementModal
        open={categoryManagementOpen}
        onClose={() => setCategoryManagementOpen(false)}
        onCategoriesUpdated={loadCategories}
      />

      {/* Event Manager functionality moved into ZoneForm component */}
    </Box>
  );
};